<template>
  <div class="checklist" :class="{ 'checklist--collapsed': isCollapsed }">
    <button class="checklist__head" @click="toggleChecklist">
      <ProgressRing
        class="checklist__progress"
        :progress="checklist.progressPercent"
      >
        {{ progressText }}
      </ProgressRing>

      <div class="checklist__head-text">
        <h2 class="checklist__title">Getting started</h2>
      </div>

      <div
        class="checklist__chevron"
        :class="{ 'checklist__chevron--collapsed': isCollapsed }"
      >
        <span class="iconfont iarrow-down"></span>
      </div>
    </button>

    <TransitionGroup class="checklist__steps" name="accordion-toggle" tag="ul">
      <ChecklistStep
        v-if="!isCollapsed"
        class="checklist__step"
        :step="{
          title: freeStepTitle,
          isCompleted: true,
        }"
        :docs="docs"
      />
      <ChecklistStep
        v-for="step in stepsToShow"
        :key="step.id"
        class="checklist__step"
        :step
        :docs="docs"
        :isNext="step.id === checklist.nextStep?.id"
      />
    </TransitionGroup>

    <ChecklistDocsPopup
      ref="docsPopup"
      :checklist
      :docs="docs.recommendations"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, Raw, ref, useTemplateRef } from 'vue'
import { emitter } from '@/services/mitt'

import { checklist as _checklist, type ChecklistView } from '@/models/checklist'
import { Mixpanel } from '@/analytics/mixpanel'
import { storage } from '@/helpers/storage'
import { StorageKeys } from '@/helpers/storage.keys'
import { ChecklistDocs } from '@/models/checklist.types'

import ChecklistStep from './ChecklistStep.vue'
import ChecklistDocsPopup from './ChecklistDocsPopup.vue'
import ProgressRing from '@/components/ui/ProgressRing.vue'

defineProps<{
  docs: ChecklistDocs
}>()

onMounted(() => {
  Mixpanel.trackChecklistShown()
  emitter.on('checklistShowPopup', showDocsPopup)
})

onUnmounted(() => {
  emitter.off('checklistShowPopup', showDocsPopup)
})

const checklist = ref<Raw<ChecklistView>>(_checklist()!)

const docsPopup = useTemplateRef('docsPopup')

// We store this in local storage so that it persists between sessions.
const isCollapsed = ref(
  storage.getItem(StorageKeys.checklistIsCollapsed19055) === 'true',
)

const freeStepTitle = computed(() =>
  checklist.value.stratum === 'trial' ? 'Start your trial' : 'Join Shortform',
)

const stepsToShow = computed(() =>
  // While the checklist is collapsed, only show the next step.
  isCollapsed.value ? [checklist.value.nextStep!] : checklist.value.stepsArray,
)

const progressText = computed(() => {
  // Add +1 from the free completed step that isn't a part of `ChecklistView`.
  const completedSteps = checklist.value.completedSteps.length + 1
  const totalSteps = checklist.value.stepsArray.length + 1

  return `${completedSteps}/${totalSteps}`
})

/**
 * Collapse / uncollapse the checklist.
 */
function toggleChecklist(): void {
  isCollapsed.value = !isCollapsed.value
  if (isCollapsed.value) {
    Mixpanel.trackChecklistDismissed()
    storage.setItem(StorageKeys.checklistIsCollapsed19055, 'true')
  } else {
    storage.removeItem(StorageKeys.checklistIsCollapsed19055)
  }
}

function showDocsPopup(): void {
  docsPopup.value?.show()
}
</script>

<style lang="scss" scoped>
.checklist {
  border-radius: 16px;
  border: 1px solid $c-border;
  background: $c-white;
  box-shadow: $shadow;
  overflow: hidden;

  @media (max-width: $w-mobile-s) {
    border-radius: 12px;
  }

  &__head {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 20px 24px;
    text-align: start;
    cursor: pointer;

    @media (max-width: $w-mobile-s) {
      padding: 12px;
    }
  }

  &__progress {
    margin-right: 16px;

    @media (max-width: $w-mobile-s) {
      margin-right: 8px;
    }
  }

  &__head-text {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-size: 24px;
    line-height: 30px;
    letter-spacing: -0.02em;
    text-wrap: balance;
    color: $c-black-2;

    @media (max-width: $w-mobile-s) {
      font-size: 18px;
      line-height: 20px;
    }
  }

  &__chevron {
    display: grid;
    place-items: center;
    --checklistChevronSize: 32px;
    width: var(--checklistChevronSize);
    height: var(--checklistChevronSize);
    font-size: var(--checklistChevronSize);
    color: $c-black-6;
    transition: transform 0.2s ease;

    @media (max-width: $w-mobile-s) {
      --checklistChevronSize: 16px;
    }

    .iconfont {
      display: block;
      width: var(--checklistChevronSize);
      height: var(--checklistChevronSize);
    }
  }

  &__chevron--collapsed {
    transform: rotate(-90deg);
  }

  &__steps {
    padding: 0;
    list-style: none;
    interpolate-size: allow-keywords;
  }

  &__step {
    overflow: hidden;
  }
}

.night-mode .checklist {
  background: $c-dark-2;
  border-color: $c-dark-3;

  &__title {
    color: $c-dark-4;
  }

  &__steps {
    border-color: $c-dark-3;
  }
}
</style>
